import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from '@/components/ui';
import { useAuth } from '@/app/providers';
import { cn, formatCompact, formatPrice } from '@/lib/utils';
import { ROUTES } from '@/config/constants';
import type { Community } from '@/types';
import { CreateCommunityModal } from '@/features/communities/components/CreateCommunityModal';
import { useOwnedCommunities } from '../../hooks/useSettingsData';
import { SettingsCard, SettingsRow } from '../SettingsPrimitives';

/** member_seq is the join counter — the closest stand-in for "member count". */
const memberCount = (c: Community) => c.member_seq ?? 0;
const isPaid = (c: Community) => c.access === 'paid' && c.price_cents > 0;
const monthlyRevenue = (c: Community) => (isPaid(c) ? (c.price_cents / 100) * memberCount(c) : 0);

export function CommunitiesTab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const { data: owned, isLoading } = useOwnedCommunities(user?.id);

  if (isLoading) {
    return (
      <SettingsCard title="Communities you own" desc="Loading…">
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </SettingsCard>
    );
  }

  const list = owned ?? [];
  const totalMembers = list.reduce((s, c) => s + memberCount(c), 0);
  const totalMRR = list.reduce((s, c) => s + monthlyRevenue(c), 0);
  const paidCount = list.filter(isPaid).length;

  return (
    <>
      <SettingsCard
        title="Communities you own"
        desc="Quick overview. Each community has its own deeper settings inside it."
        action={
          list.length > 0 && (
            <Button type="button" variant="subtle" size="sm" onClick={() => setCreateOpen(true)}>
              + Create another
            </Button>
          )
        }
      >
        {list.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center text-[13px] text-mid">
            You don't own any communities yet.
            <Button type="button" variant="subtle" size="sm" onClick={() => setCreateOpen(true)}>
              Create your first community
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-4 grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2.5">
              <Stat label="Communities" value={String(list.length)} />
              <Stat label="Total members" value={formatCompact(totalMembers)} />
              <Stat
                label="Est. MRR"
                value={`$${Math.round(totalMRR).toLocaleString()}`}
                note={`${paidCount} paid · across all`}
                highlight
              />
            </div>

            <div className="flex flex-col gap-2">
              {list.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-edge bg-white/[0.02] p-3"
                >
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-extrabold"
                    style={{ background: `${c.color}33`, color: c.color }}
                  >
                    {c.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-semibold text-hi">{c.name}</div>
                    <div className="mt-0.5 truncate text-[11px] text-lo">
                      {c.category || '—'} · {formatCompact(memberCount(c))} members ·{' '}
                      {isPaid(c) ? `${formatPrice(c.price_cents)}/mo` : 'Free'}
                      {monthlyRevenue(c) > 0 &&
                        ` · ~$${Math.round(monthlyRevenue(c)).toLocaleString()} MRR`}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="subtle"
                    size="sm"
                    onClick={() => navigate(ROUTES.community(c.id))}
                  >
                    Open
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </SettingsCard>

      {list.length > 0 && (
        <SettingsCard
          title="Payouts & billing"
          desc="For paid communities. Per-community payment settings live inside each community."
        >
          <SettingsRow
            title="Stripe Connect"
            desc="Required for paid memberships. Handles subscriptions, refunds, taxes."
            control={
              <Button type="button" variant="subtle" size="sm">
                Connect
              </Button>
            }
          />
        </SettingsCard>
      )}

      <CreateCommunityModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}

function Stat({
  label,
  value,
  note,
  highlight,
}: {
  label: string;
  value: string;
  note?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-[10px] border p-3',
        highlight ? 'border-white/12 bg-white/[0.04]' : 'border-edge bg-white/[0.03]',
      )}
    >
      <div className="text-[11px] font-bold uppercase tracking-[0.4px] text-lo">{label}</div>
      <div className="mt-1 text-[22px] font-extrabold text-hi">{value}</div>
      {note && <div className="mt-0.5 text-[10px] text-lo">{note}</div>}
    </div>
  );
}
